import { useState } from 'react';

import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { formatMonthLabel } from './cashflowFeeExposureHelpers';
import { useUserName } from './useUserName';

export function formatScenarioDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
}

export function scenarioWindowText(scenario) {
  if (!scenario?.startMonth || !scenario?.endMonth) return '';
  return `${formatMonthLabel(scenario.startMonth)} – ${formatMonthLabel(scenario.endMonth)}`;
}

function savedText(scenario, userName) {
  const when = formatScenarioDate(scenario.updatedAt || scenario.createdAt);
  const who = userName(scenario.updatedBy || scenario.createdBy);
  return who ? `${when} by ${who}` : when;
}

// SourcesUsesScenarioList is the first page of Sources & Uses: Main, then the scenarios.
function SourcesUsesScenarioList({
  scenarios,
  loading,
  busyId,
  onOpenMain,
  onNew,
  onOpen,
  onFinish,
  onDelete,
}) {
  const [newOpen, setNewOpen] = useState(false);
  const [name, setName] = useState('');
  const [deleting, setDeleting] = useState(null);
  const userName = useUserName();
  const trimmed = name.trim();

  const startNew = () => {
    if (!trimmed) return;
    onNew(trimmed);
    setNewOpen(false);
    setName('');
  };

  return (
    <Box>
      <Box borderWidth="1px" borderColor="brand.200" bg="brand.50" borderRadius="12px" p={4} mb={6}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
          <Box>
            <Flex align="center" gap={2} mb={1}>
              <Text fontSize="lg" fontWeight={700}>Main</Text>
              <Badge colorScheme="gray">Not a scenario</Badge>
            </Flex>
            <Text fontSize="sm" color="gray.600">
              The current data and the source of truth. Edit customers and company cash here and press Save &amp; Run.
              Nothing is saved as a scenario.
            </Text>
          </Box>
          <Button colorScheme="brand" onClick={onOpenMain}>Open Main</Button>
        </Flex>
      </Box>

      <Flex justify="space-between" align="center" wrap="wrap" gap={3} mb={3}>
        <Box>
          <Text fontSize="sm" fontWeight={700} letterSpacing="0.06em">SCENARIOS</Text>
          <Text fontSize="xs" color="gray.500">
            A new scenario starts from Main. Each Save &amp; Run updates Main and the scenario. It is finished when you
            leave it, and can&apos;t be changed after that.
          </Text>
        </Box>
        <Button size="sm" colorScheme="brand" variant="outline" onClick={() => setNewOpen(true)}>New scenario</Button>
      </Flex>

      {loading && <Spinner size="sm" />}
      {!loading && !scenarios.length && (
        <Text fontSize="sm" color="gray.500">No scenarios yet.</Text>
      )}
      {!loading && scenarios.length > 0 && (
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Last saved</Th>
              <Th>Months</Th>
              <Th />
            </Tr>
          </Thead>
          <Tbody>
            {scenarios.map((scenario) => (
              <Tr key={scenario.id}>
                <Td fontWeight={600}>{scenario.name}</Td>
                <Td>
                  {scenario.frozen
                    ? <Badge colorScheme="green">Finished</Badge>
                    : <Badge colorScheme="orange">Not finished</Badge>}
                </Td>
                <Td>{savedText(scenario, userName)}</Td>
                <Td>{scenarioWindowText(scenario)}</Td>
                <Td>
                  <Flex gap={2} justify="flex-end">
                    <Button size="xs" variant="outline" isDisabled={busyId === scenario.id} onClick={() => onOpen(scenario)}>
                      {scenario.frozen ? 'Open' : 'Continue'}
                    </Button>
                    {!scenario.frozen && (
                      <Button size="xs" variant="outline" isLoading={busyId === scenario.id} onClick={() => onFinish(scenario)}>
                        Finish
                      </Button>
                    )}
                    <Button size="xs" variant="outline" colorScheme="red" isDisabled={busyId === scenario.id} onClick={() => setDeleting(scenario)}>
                      Delete
                    </Button>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      )}

      <Modal isOpen={newOpen} onClose={() => setNewOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New scenario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="gray.600" mb={3}>
              Starts from Main. The scenario is saved when you press Save &amp; Run.
            </Text>
            <Input
              size="sm"
              aria-label="Scenario name"
              placeholder="Name"
              value={name}
              maxLength={120}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') startNew();
              }}
            />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" colorScheme="brand" isDisabled={!trimmed} onClick={startNew}>Continue</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={!!deleting} onClose={() => setDeleting(null)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete scenario</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>{`Delete scenario ${deleting?.name || ''}? This can't be undone.`}</Text>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="ghost" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button
              size="sm"
              colorScheme="red"
              onClick={() => {
                const target = deleting;
                setDeleting(null);
                onDelete(target);
              }}
            >
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

SourcesUsesScenarioList.propTypes = {
  scenarios: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  busyId: PropTypes.string,
  onOpenMain: PropTypes.func.isRequired,
  onNew: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default SourcesUsesScenarioList;
